import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { buildItemColDefs } from '../components/itemColDefs'
import type { CellAnchor } from '../components/priceColDefs'
import type { Request, SourceType } from '../../../shared/api/types'
import type { ProcurementUser } from '../../../shared/api/users.api'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'
import type { SelectedPrice } from './useKpMode'

type User = { id?: string; role?: string } | null

type Params = {
  request: Request | undefined
  user: User
  requestId: string
  procUsers: ProcurementUser[]
  handleProcNoteHeight: () => void
  cnyRate: number | null
  markupConstants: MarkupConstants | null
  /** Курсы и коэффициенты наценки загружены. Входит в deps useMemo: после
      дозагрузки справочников колонки пересобираются и прочерки сменяются ценами. */
  pricingReady: boolean
  kpMode: boolean
  selectedPrices: Map<string, SelectedPrice>
  onSelectPrice: (
    itemId: string,
    col: string,
    matrix: string,
    cost: number,
    /** Цена в ячейке в её валюте — по ней подбирается поставщик строки КП. */
    cellValue: number,
  ) => void
  onOpenQuotes: (
    itemId: string,
    sourceType: SourceType,
    matrixLabel: string,
    anchor: CellAnchor,
  ) => void
}

export function useItemColDefs({
  request,
  user,
  requestId,
  procUsers,
  handleProcNoteHeight,
  cnyRate,
  markupConstants,
  pricingReady,
  kpMode,
  selectedPrices,
  onSelectPrice,
  onOpenQuotes,
}: Params) {
  const queryClient = useQueryClient()

  const isAdmin = user?.role === 'ADMIN'
  const isProcurement = user?.role === 'PROCUREMENT'

  // Извлекаем булево «заявка загружена» — это стабильная зависимость для useMemo,
  // в отличие от !!request, которое eslint трактует как сложное выражение в deps.
  const hasRequest = !!request
  return useMemo(() => {
    if (!request) return []
    return buildItemColDefs({
      isAdmin,
      userRole: user?.role,
      userId: user?.id,
      isProcurement,
      requestId,
      queryClient,
      procUsers,
      handleProcNoteHeight,
      cnyRate,
      markupConstants,
      pricingReady,
      requestManager: request.manager
        ? { id: request.managerId, name: request.manager.name, email: request.manager.email }
        : null,
      clientCategory: request.client?.category,
      kpMode,
      selectedPrices,
      onSelectPrice,
      onOpenQuotes,
    })
    // Зависимости мемо: специально не включаем `request` целиком (меняется при любом
    // refetch) — достаточно факта его наличия. `queryClient`, `user?.id`, `user?.role`,
    // колбэки и `requestManager` стабильны на время сессии или пересоздаются вместе с
    // указанными ниже зависимостями. Менять полный список — это вызовет пересборку
    // colDefs на каждое обновление заявки и сбросит состояние ag-grid (выделение, фильтры).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- сознательно: см. комментарий выше
  }, [
    isAdmin,
    isProcurement,
    requestId,
    procUsers,
    kpMode,
    selectedPrices,
    cnyRate,
    markupConstants,
    pricingReady,
    hasRequest,
  ])
}
